import {
  tenantRoleSchema,
  type IssuedInvitation,
  type Membership,
  type TenantRole,
} from '@avrahami/contracts';
import {
  Alert,
  Badge,
  Button,
  Card,
  Cell,
  ConfirmDialog,
  CopyField,
  formatDate,
  formatDateTime,
  invitationLink,
  Loading,
  PageHeader,
  SelectField,
  Table,
  TextField,
} from '@avrahami/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type SyntheticEvent } from 'react';
import { useCan, useMe } from '../api/session';
import { keys, membersQuery, tenantApi, useStoreTimeZone } from '../api/tenant';
import { mergeErrors, FormStatus } from '../components/fields';
import { he } from '../i18n/he';
import { assignableRoles, canManageMember } from '../lib/access';
import { inviteRequest, type InviteDraft } from '../lib/drafts';
import { errorMessage, NO_ERRORS, type FieldErrors } from '../lib/forms';

/** This app's own invitation page: the link a new member opens to set a password. */
function memberInvitationLink(token: string): string {
  return invitationLink(window.location.origin, token);
}

/** The store's team: who has access, with what role; invite, re-invite, change, remove. */
export function StaffPage() {
  const me = useMe();
  const canManage = useCan('store.staff.manage');
  const members = useQuery(membersQuery());
  const timeZone = useStoreTimeZone();
  const [issued, setIssued] = useState<{ email: string; invitation: IssuedInvitation | null }>();
  const actorRole = me?.activeTenant?.role;

  return (
    <>
      <PageHeader title={he.staff.title} />
      <div className="flex flex-col gap-6">
        {canManage && actorRole !== undefined ? (
          <InviteForm
            actorRole={actorRole}
            onInvited={(email, invitation) => {
              setIssued({ email, invitation });
            }}
          />
        ) : null}
        {issued === undefined ? null : issued.invitation === null ? (
          <Alert tone="info">{he.staff.existingAccount}</Alert>
        ) : (
          <Card title={`${he.staff.inviteTitle}: ${issued.email}`}>
            <div className="flex flex-col gap-2">
              <p className="text-sm text-slate-600">{he.staff.inviteBody}</p>
              <p className="text-xs text-slate-500">
                {he.invitation.expires} {formatDateTime(issued.invitation.expiresAt, timeZone)}
              </p>
              <CopyField
                label={he.staff.inviteTitle}
                value={memberInvitationLink(issued.invitation.token)}
                testId="invitation-link"
              />
            </div>
          </Card>
        )}
        <Card>
          {members.isPending ? <Loading /> : null}
          {members.isError ? <Alert>{errorMessage(members.error)}</Alert> : null}
          {members.data === undefined ? null : (
            <Table
              head={[
                he.staff.name,
                he.staff.email,
                he.staff.role,
                he.staff.access,
                he.staff.joined,
                ...(canManage ? [he.common.actions] : []),
              ]}
            >
              {members.data.items.map((member) => (
                <MemberRow
                  key={member.id}
                  member={member}
                  isSelf={member.userId === me?.user.id}
                  actorRole={canManage ? actorRole : undefined}
                  onInvitation={(invitation) => {
                    setIssued({ email: member.email, invitation });
                  }}
                />
              ))}
            </Table>
          )}
        </Card>
      </div>
    </>
  );
}

function InviteForm({
  actorRole,
  onInvited,
}: {
  actorRole: TenantRole;
  onInvited: (email: string, invitation: IssuedInvitation | null) => void;
}) {
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState<InviteDraft>({
    email: '',
    displayName: '',
    role: 'TENANT_STAFF',
  });
  const [localErrors, setLocalErrors] = useState<FieldErrors>(NO_ERRORS);
  const invite = useMutation({
    mutationFn: tenantApi.inviteMember,
    onSuccess: async (result) => {
      onInvited(result.membership.email, result.invitation);
      setDraft((current) => ({ ...current, email: '', displayName: '' }));
      await queryClient.invalidateQueries({ queryKey: keys.members() });
    },
  });
  const onSubmit = (event: SyntheticEvent) => {
    event.preventDefault();
    const parsed = inviteRequest(draft);
    if (!parsed.ok) {
      setLocalErrors(parsed.errors);
      return;
    }
    setLocalErrors(NO_ERRORS);
    invite.mutate(parsed.value);
  };
  const errors = mergeErrors(localErrors, invite.error);
  return (
    <Card title={he.staff.invite}>
      <form
        onSubmit={onSubmit}
        className="flex flex-col gap-3"
        noValidate
        autoComplete="off"
        data-testid="invite-form"
      >
        <div className="flex flex-wrap items-end gap-3">
          <TextField
            label={he.staff.email}
            name="email"
            type="email"
            dir="ltr"
            autoComplete="off"
            className="w-64"
            value={draft.email}
            error={errors['email']}
            onChange={(event) => {
              setDraft((current) => ({ ...current, email: event.target.value }));
            }}
          />
          <TextField
            label={he.staff.name}
            name="displayName"
            autoComplete="off"
            className="w-48"
            maxLength={120}
            value={draft.displayName}
            error={errors['displayName']}
            onChange={(event) => {
              setDraft((current) => ({ ...current, displayName: event.target.value }));
            }}
          />
          <SelectField
            label={he.staff.role}
            name="role"
            value={draft.role}
            onChange={(event) => {
              const role = tenantRoleSchema.parse(event.target.value);
              setDraft((current) => ({ ...current, role }));
            }}
          >
            {assignableRoles(actorRole).map((role) => (
              <option key={role} value={role}>
                {he.roles[role]}
              </option>
            ))}
          </SelectField>
          <Button type="submit" busy={invite.isPending}>
            {he.staff.submit}
          </Button>
        </div>
        <FormStatus error={invite.error} localInvalid={Object.keys(localErrors).length > 0} />
      </form>
    </Card>
  );
}

function MemberRow({
  member,
  isSelf,
  actorRole,
  onInvitation,
}: {
  member: Membership;
  isSelf: boolean;
  /** The actor's role when they may manage staff; undefined for read-only viewers. */
  actorRole: TenantRole | undefined;
  onInvitation: (invitation: IssuedInvitation) => void;
}) {
  const queryClient = useQueryClient();
  const [confirmRemove, setConfirmRemove] = useState(false);
  const timeZone = useStoreTimeZone();
  const manageable = actorRole !== undefined && canManageMember(actorRole, member.role, isSelf);
  const roles = actorRole === undefined ? [] : assignableRoles(actorRole);
  const refresh = () => queryClient.invalidateQueries({ queryKey: keys.members() });
  const changeRole = useMutation({
    mutationFn: (role: TenantRole) => tenantApi.changeRole(member.id, role),
    onSuccess: refresh,
  });
  const remove = useMutation({
    mutationFn: () => tenantApi.removeMember(member.id),
    onSuccess: async () => {
      setConfirmRemove(false);
      await refresh();
    },
  });
  const reissue = useMutation({
    mutationFn: () => tenantApi.reissueInvitation(member.id),
    onSuccess: async (invitation) => {
      onInvitation(invitation);
      await refresh();
    },
  });
  const error = changeRole.error ?? reissue.error;

  return (
    <tr data-testid="member-row" data-email={member.email} data-role={member.role}>
      <Cell>
        {member.displayName}{' '}
        {isSelf ? <span className="text-xs text-slate-500">{he.staff.you}</span> : null}
      </Cell>
      <Cell className="ltr">{member.email}</Cell>
      <Cell>
        {manageable ? (
          <select
            aria-label={`${he.staff.role}: ${member.displayName}`}
            value={member.role}
            disabled={changeRole.isPending}
            onChange={(event) => {
              changeRole.mutate(tenantRoleSchema.parse(event.target.value));
            }}
            className="rounded-md border border-slate-300 bg-white px-2 py-1 text-sm"
          >
            {roles.map((option) => (
              <option key={option} value={option}>
                {he.roles[option]}
              </option>
            ))}
          </select>
        ) : (
          he.roles[member.role]
        )}
      </Cell>
      <Cell>
        {member.needsPasswordSetup ? (
          <div className="flex flex-col items-start gap-1">
            <Badge tone="amber">{he.staff.needsPassword}</Badge>
            <span className="text-xs text-slate-500">
              {member.invitationExpiresAt === null
                ? he.staff.noInvitation
                : `${he.staff.invitationUntil} ${formatDateTime(member.invitationExpiresAt, timeZone)}`}
            </span>
            {manageable ? (
              <Button
                tone="secondary"
                busy={reissue.isPending}
                onClick={() => {
                  reissue.mutate();
                }}
              >
                {he.staff.newInvitation}
              </Button>
            ) : null}
          </div>
        ) : (
          <Badge tone="green">{he.staff.passwordSet}</Badge>
        )}
      </Cell>
      <Cell>{formatDate(member.createdAt, timeZone)}</Cell>
      {actorRole === undefined ? null : (
        <Cell>
          {manageable ? (
            <Button
              tone="secondary"
              onClick={() => {
                remove.reset();
                setConfirmRemove(true);
              }}
            >
              {he.staff.remove}
            </Button>
          ) : null}
          {error === null ? null : <Alert>{errorMessage(error)}</Alert>}
          <ConfirmDialog
            open={confirmRemove}
            title={he.staff.removeTitle}
            confirmLabel={he.staff.remove}
            tone="danger"
            busy={remove.isPending}
            onConfirm={() => {
              remove.mutate();
            }}
            onClose={() => {
              setConfirmRemove(false);
            }}
          >
            <p>
              {member.displayName} (<span className="ltr">{member.email}</span>)
            </p>
            <p className="mt-2">{he.staff.removeBody}</p>
            {remove.isError ? <Alert>{errorMessage(remove.error)}</Alert> : null}
          </ConfirmDialog>
        </Cell>
      )}
    </tr>
  );
}
